import { interpolatePlasma } from "d3";
import type { Feature, FeatureCollection, MultiPolygon } from "geojson";

import type { RowGeoResponse, RowSnipResponse } from "api/model";
import type { PlotColorBy } from "defs";

const getColorMetric = (
  snip: RowSnipResponse,
  color: PlotColorBy,
): number | null => {
  if (color === "emerg") {
    return snip.emergence_count ?? null;
  }
  if (color === "rc") {
    return snip.canopy_cover ?? null;
  }
  return null;
};

const getPlasmaColorsById = (
  entries: readonly { id: number; value: number }[],
): ReadonlyMap<number, string> => {
  if (entries.length === 0) {
    return new Map();
  }

  const values = entries.map((entry) => entry.value);
  const maximum = Math.max(...values);
  const minimum = Math.min(...values);
  const range = maximum - minimum;

  return new Map(
    entries.map(({ id, value }) => {
      const t = range === 0 ? 0.5 : (value - minimum) / range;
      return [id, interpolatePlasma(t)];
    }),
  );
};

/** Plasma color per row id, scaled min-to-max across every row that has a value. */
export const getRowAnalysisFillColors = (
  rowColor: PlotColorBy,
  rowSnips: readonly RowSnipResponse[],
): ReadonlyMap<number, string> => {
  if (rowColor === "none") {
    return new Map();
  }

  return getPlasmaColorsById(
    rowSnips.flatMap((snip) => {
      const value = getColorMetric(snip, rowColor);
      return value == null ? [] : [{ id: snip.row_id, value }];
    }),
  );
};

/** A plot's analysis values, aggregated from its row snips. */
export interface PlotSnipAggregate {
  /** Mean row cover across rows that have one; null when none do. */
  rowCover: number | null;
  /** Sum of emergence counts across rows that have one; null when none do. */
  emergenceCount: number | null;
}

/**
 * Aggregates row snips into one value per plot id: emergence counts are summed
 * and row cover is averaged. Rows without a value are ignored. Shared by the
 * map plot fill and the analysis table so the two always agree.
 */
export const aggregateRowSnipsByPlot = (
  rowSnips: readonly RowSnipResponse[],
): ReadonlyMap<number, PlotSnipAggregate> => {
  const accumulators = new Map<
    number,
    { coverCount: number; coverSum: number; emergence: number | null }
  >();
  for (const snip of rowSnips) {
    const accumulator = accumulators.get(snip.plot_id) ?? {
      coverCount: 0,
      coverSum: 0,
      emergence: null,
    };
    if (snip.emergence_count != null) {
      accumulator.emergence =
        (accumulator.emergence ?? 0) + snip.emergence_count;
    }
    if (snip.canopy_cover != null) {
      accumulator.coverCount += 1;
      accumulator.coverSum += snip.canopy_cover;
    }
    accumulators.set(snip.plot_id, accumulator);
  }

  return new Map(
    [...accumulators].map(([plotId, accumulator]) => [
      plotId,
      {
        emergenceCount: accumulator.emergence,
        rowCover:
          accumulator.coverCount === 0
            ? null
            : accumulator.coverSum / accumulator.coverCount,
      },
    ]),
  );
};

/**
 * Plasma color per plot id, scaled min-to-max across plots. A plot's value is
 * its aggregate from {@link aggregateRowSnipsByPlot}; plots without a value for
 * the chosen metric are left uncolored.
 */
export const getPlotAnalysisFillColors = (
  plotColor: PlotColorBy,
  rowSnips: readonly RowSnipResponse[],
): ReadonlyMap<number, string> => {
  if (plotColor === "none") {
    return new Map();
  }

  return getPlasmaColorsById(
    [...aggregateRowSnipsByPlot(rowSnips)].flatMap(([id, aggregate]) => {
      const value =
        plotColor === "rc" ? aggregate.rowCover : aggregate.emergenceCount;
      return value == null ? [] : [{ id, value }];
    }),
  );
};

/** One filled polygon per row that has a color; rows without one are omitted. */
export const toRowAnalysisFillGeoJson = (
  rowsGeo: readonly RowGeoResponse[],
  fillColorByRowId: ReadonlyMap<number, string>,
  dimmedRowIds?: ReadonlySet<number>,
): FeatureCollection<MultiPolygon> => ({
  type: "FeatureCollection",
  features: rowsGeo.flatMap((row): Feature<MultiPolygon>[] => {
    const fillColor = fillColorByRowId.get(row.id);
    if (fillColor == null || row.boundary == null) {
      return [];
    }
    return [
      {
        type: "Feature",
        geometry: row.boundary,
        properties: {
          dimmed: dimmedRowIds?.has(row.id) ?? false,
          fillColor,
          kind: "row-fill",
          rowId: row.id,
        },
      },
    ];
  }),
});

/** Adds `fillColor` to each saved-plot grid cell that has a color. */
export const applyPlotAnalysisFillColors = <
  T extends {
    features: ReadonlyArray<{
      properties?: { kind?: string; plotGeoId?: number };
    }>;
  },
>(
  geoJson: T,
  fillColorByPlotId: ReadonlyMap<number, string>,
): T => {
  if (fillColorByPlotId.size === 0) {
    return geoJson;
  }

  return {
    ...geoJson,
    features: geoJson.features.map((feature) => {
      const plotGeoId = feature.properties?.plotGeoId;
      if (
        feature.properties?.kind !== "grid-cell" ||
        typeof plotGeoId !== "number"
      ) {
        return feature;
      }
      const fillColor = fillColorByPlotId.get(plotGeoId);
      if (fillColor == null) {
        return feature;
      }
      return {
        ...feature,
        properties: { ...feature.properties, fillColor },
      };
    }),
  };
};
